import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, inject } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { ABTest } from '@freelancer/abtest';
import type { UserDepositMethod } from '@freelancer/datastore/collections';
import {
  FREELANCER_HTTP_CONFIG,
  FreelancerHttpConfig,
} from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import type { PaymentsMessage } from '@freelancer/payments-messaging';
import {
  PaymentsErrorType,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { Pwa } from '@freelancer/pwa';
import { UserAgent } from '@freelancer/user-agent';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { DepositMethodApi } from 'api-typings/payments/payments';
import type { Observable } from 'rxjs';
import { fromEvent, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import type { DepositFormType, MessageEventFilter } from './types';
import {
  DepositForm,
  InterframeDepositResponseType,
  methodSelectionRadioControl,
} from './types';

@Injectable({
  providedIn: 'root',
})
export class PaymentsUtils {
  private location = inject(Location);
  private freelancerHttpConfig = inject<FreelancerHttpConfig>(
    FREELANCER_HTTP_CONFIG,
  );
  private breakpointObserver = inject(BreakpointObserver);
  private abTest = inject(ABTest);
  private userAgent = inject(UserAgent);
  private pwa = inject(Pwa);

  // Helper function for message handling
  getFilteredMessageStream(
    messageEventFilter: MessageEventFilter,
  ): Observable<MessageEvent['data']> {
    return fromEvent<MessageEvent>(window, 'message').pipe(
      filter(
        event =>
          (event.origin === this.location.origin ||
            event.origin === this.freelancerHttpConfig.baseUrl || // TODO: T267853 - find a way to inject `environment`
            this.location.hostname === 'localhost') &&
          messageEventFilter(event),
      ),
      map(event => event.data),
    );
  }

  // Helper function for message handling
  getExternalServiceMessageStream(
    externalOrigin: string,
    messageEventFilter: MessageEventFilter,
  ): Observable<MessageEvent['data']> {
    return fromEvent<MessageEvent>(window, 'message').pipe(
      filter(
        event => event.origin === externalOrigin && messageEventFilter(event),
      ),
      map(event => event.data),
    );
  }

  listenForDepositResult(): Observable<MessageEvent['data']> {
    return this.getFilteredMessageStream(
      event =>
        !!event.data.type &&
        Object.values(InterframeDepositResponseType).includes(event.data.type),
    ).pipe(take(1));
  }

  handleInterframeDepositResult(data: any): PaymentsMessage | undefined {
    switch (data.type) {
      case InterframeDepositResponseType.SUCCESS:
        return {
          paymentsStatus: PaymentsResultStatus.SUCCESS,
        };
      case InterframeDepositResponseType.ERROR_AUTH:
        return {
          errorType: PaymentsErrorType.ERROR_AUTH,
        };
      case InterframeDepositResponseType.BAD_BIN:
        return {
          errorType: PaymentsErrorType.ERROR,
          errorCode: ErrorCodeApi.BIN_NON_RECURRING,
        };
      case InterframeDepositResponseType.ERROR:
      case InterframeDepositResponseType.ERROR_DETAILED:
      case InterframeDepositResponseType.ERROR_BIN:
      case InterframeDepositResponseType.ERROR_CART:
      case InterframeDepositResponseType.FAILURE:
        return {
          errorMessage: data.human_msg
            ? data.human_msg
            : data.message
              ? data.message
              : '',
          errorCode: data.error_code,
          eventId: data.uuid,
          errorType: PaymentsErrorType.ERROR,
        };
      case InterframeDepositResponseType.PENDING:
        return {
          paymentsMessage: data.message ? data.message : '',
          paymentsStatus: PaymentsResultStatus.PENDING,
        };
      case InterframeDepositResponseType.CANCELLED:
        return {
          paymentsStatus: PaymentsResultStatus.INTERRUPTED,
        };
      default:
        return {
          errorType: PaymentsErrorType.ERROR,
        };
    }
  }

  getCancelUrl(): string {
    return `${this.location.origin}/payments/deposit/cancel.php?status=cancel`;
  }

  /**
   * Helper for registering the method forms in parent form.
   * @param methodFormGroup Specific method form group to be registered
   * @param depositFormGroup Parent Form Group to register into
   * @param id key to register the control as (method id)
   */
  registerFormGroup(
    // This cannot be easily typed since it is dynamic
    // eslint-disable-next-line local-rules/no-untyped-form
    methodFormGroup: FormGroup,
    depositFormGroup: FormGroup<DepositFormType>,
    id: string,
  ): void {
    if (
      depositFormGroup.controls[DepositForm.RADIO].value[
        methodSelectionRadioControl
      ] !== id
    ) {
      methodFormGroup.disable();
    }
    const depositMethodsFormGroup =
      depositFormGroup.controls[DepositForm.METHODS];
    depositMethodsFormGroup.setControl(id, methodFormGroup);
  }

  getMethodLogoPath(method: DepositMethodApi | string): string {
    // iDEAL rebranding is ongoing
    if (method === DepositMethodApi.IDEAL) {
      return `payments/ideal-wero.svg`;
    }
    if (method === DepositMethodApi.GOOGLEPAY_REFERENCE) {
      return `payments/googlepay.svg`;
    }
    if (method === DepositMethodApi.APPLEPAY_REFERENCE) {
      return `payments/applepay.svg`;
    }
    return `payments/${method}.svg`;
  }

  /**
   * Helper for turning on & off certain elements of the Deposit page based on
   * the viewport size, in order to make it mobile responsive.
   *
   * IMPORTANT: using `BreakpointObserver` here breaks server-side rendering,
   * i.e. we still have to remove it if we ever want to server-side render the
   * Deposit page. This will likely require a larger refactor of the deposit
   * page / a design change, as we can't load twice the billing iframes for
   * instance.
   */
  isMobileDeposit(): Observable<boolean> {
    return this.breakpointObserver
      .observe([Breakpoints.Small, Breakpoints.XSmall])
      .pipe(map(m => m.matches));
  }

  /**
   * Whether the current user is enrolled in the test group of the
   * disable future Paypal agreements A/B test.
   */
  isEnrolledInDisablePaypalTest$(): Observable<boolean> {
    return this.abTest
      .getUserExperimentVariation('T340796-disable-future-paypal-noop')
      .pipe(map(variation => variation === 'test'));
  }

  /**
   * Strips Paypal if the user is in the disable future Paypal agreements test group.
   * Only hits the A/B test when Paypal is present in `methods`.
   */
  filterPaypalByRolloutEnrollment$(
    methods: readonly UserDepositMethod[],
  ): Observable<readonly UserDepositMethod[]> {
    if (!methods.some(method => method.methodId === DepositMethodApi.PAYPAL)) {
      return of(methods);
    }

    return this.isEnrolledInDisablePaypalTest$().pipe(
      map(enrolled =>
        enrolled
          ? methods.filter(
              method => method.methodId !== DepositMethodApi.PAYPAL,
            )
          : methods,
      ),
    );
  }

  /**
   * Whether the current browser & OS supported Apple Pay before we started
   * loading the Apple Pay JS SDK everywhere, i.e. Apple Pay is available here
   * regardless of the extension A/B test.
   *
   * TODO: T333835 - Remove this once the A/B test is complete
   */
  private isNativeApplePayEnvironment(): boolean {
    return (
      this.userAgent.getUserAgent().getBrowser().name?.includes('Safari') ||
      this.userAgent.getUserAgent().getOS().name === 'iOS' ||
      this.pwa.getPlatform() === 'ios'
    );
  }

  /**
   * Whether the current user is enrolled in the test group of the A/B test
   * extending Apple Pay to non-Apple browsers and operating systems.
   *
   * TODO: T333835 - Remove this once the A/B test is complete
   */
  isEnrolledInApplePayExtensionTest$(): Observable<boolean> {
    return this.abTest
      .getUserExperimentVariation(
        'T333835-extend-apple-pay-to-non-apple-browser-or-os-v2-noop',
      )
      .pipe(map(variation => variation === 'test'));
  }

  /**
   * Strips Apple Pay unless the user is in the extension test group.
   * Only hits the A/B test when Apple Pay is present in `methods`, i.e. the
   * backend considers the user eligible, and the browser & OS didn't already
   * support Apple Pay before the extension.
   *
   * TODO: T333835 - Remove this once the A/B test is complete
   */
  filterApplePayByExtensionEnrollment$(
    methods: readonly UserDepositMethod[],
  ): Observable<readonly UserDepositMethod[]> {
    if (
      !methods.some(method => method.methodId === DepositMethodApi.APPLEPAY) ||
      this.isNativeApplePayEnvironment()
    ) {
      return of(methods);
    }

    return this.isEnrolledInApplePayExtensionTest$().pipe(
      map(enrolled =>
        enrolled
          ? methods
          : methods.filter(
              method => method.methodId !== DepositMethodApi.APPLEPAY,
            ),
      ),
    );
  }
}
